import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Pencil,
    Phone,
    MapPin,
    Clock,
    Target,
    Calendar,
    Info,
    CheckCircle2,
    XCircle,
    TrendingUp,
    Star,
    ChefHat,
    User,
    Mail,
    Globe,
    ShoppingBag,
    Banknote
} from 'lucide-react';

import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { StatusBadge } from '@/components/common/StatusBadge';
import { CommonCard } from '@/components/common/CommonCard';
import { NoDataFound } from '@/components/common/NoDataFound';
import { SectionCard } from '@/components/common/SectionCard';
import { DetailItem } from '@/components/common/DetailItem';
import { CommonSelect, CommonSelectOption } from '@/components/common/CommonSelect';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IStore, IStoreResponse } from '@/interfaces/Store/store.interface';
import { formatDateTime, formatTime12Hour, showToastNotification, getCookies, getRole, formatRand } from '@/utility/helper';
import { RoutesLink, getRolePrefix } from '@/utility/RouteLinks';
import { useAppSelector, RootState } from '@/redux/store';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';

const GOOGLE_API_KEY = import.meta.env.VITE_APP_GOOGLE_API_KEY || '';
const mapContainerStyle = {
    width: '100%',
    height: '200px',
    borderRadius: '12px'
};

export const StoreDetails: React.FC = () => {
    const { canEdit } = usePermissions();
    const { id } = useParams<{ id: string }>();
    const navigate = useRoleNavigate();
    const backNavigate = useNavigate();
    const [store, setStore] = useState<IStore | null>(null);
    const [loading, setLoading] = useState(true);
    const [statusLoading, setStatusLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_API_KEY,
        libraries: ['places'] as any
    });

    const STORE_STATUS_OPTIONS: CommonSelectOption[] = [
        { value: 'created', label: 'Created', color: 'bg-blue-200 shadow-[0_0_8px_rgba(34,197,94,0.5)]' },
        { value: 'online', label: 'Online', color: 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]' },
        { value: 'busy', label: 'Busy', color: 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]' },
        { value: 'offline', label: 'Offline', color: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' },
    ];

    const fetchStoreDetails = async () => {
        setLoading(true);
        try {
            const response = await callApi<IStoreResponse>(
                `${API_URL + API_ENDPOINT.stores}/detail/${id}`,
                { method: 'GET' }
            );
            setStore(response?.data);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (newStatus: 'online' | 'offline' | 'busy' | 'created') => {
        if (!store || statusLoading) return;
        const formData = new FormData();

        // Trim and append text fields
        formData.append('storeId', id);
        formData.append('status', newStatus);
        setStatusLoading(true);
        try {
            await callApi(
                `${API_URL + API_ENDPOINT.stores}/update`,
                {
                    method: 'PUT',
                    data: formData,
                    multipart: true
                }
            );

            // Optimistic Update
            setStore(prev => prev ? { ...prev, status: newStatus } : null);
            showToastNotification('success', `Store is now ${newStatus}`);
        } catch (error) {
            ThrowError(error);
        } finally {
            setStatusLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchStoreDetails();
        }
    }, [id]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    if (!store) {
        return (
            <Layout>
                <NoDataFound
                    title="Store Not Found"
                    description="The store you are looking for might have been moved, renamed, or no longer exist in our records."
                    onAction={() => backNavigate(-1)}
                />
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-10 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
                {/* Header Section */}
                <MainHeading
                    title={
                        <div className="flex items-center gap-4">
                            {store.media?.find(m => m.referenceType === 'store_logo')?.url ? (
                                <div className="w-12 h-12 rounded-xl overflow-hidden border border-border/40 shadow-sm bg-background">
                                    <img
                                        src={`${API_URL}/${store.media.find(m => m.referenceType === 'store_logo')!.url}`}
                                        alt={store.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            ) : (
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                                    <ChefHat className="w-6 h-6" />
                                </div>
                            )}
                            <div className="flex flex-col">
                                <span className="text-xl font-bold">{store.name}</span>
                                <div className="scale-90 origin-left mt-0.5">
                                    <StatusBadge status={store.status} />
                                </div>
                            </div>
                        </div>
                    }
                    description={
                        <div className="flex items-center gap-2 text-muted-foreground/80 mt-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span className="text-sm">Added {formatDateTime(store.createdAt, 'default')}</span>
                        </div>
                    }
                    showBack={true}
                    onBack={() => backNavigate(-1)}
                    actionLabel={canEdit(RESOURCES.STORE) && "Edit Store"}
                    actionIcon={canEdit(RESOURCES.STORE) && <Pencil className="w-4 h-4" />}
                    onAction={() => navigate(RoutesLink.EditStore.replace(':id', store.id))}
                />

                {/* Banner Image Hero */}
                {store.media?.find(m => m.referenceType === 'store_banner')?.url && (
                    <div className="w-full h-[250px] md:h-[350px] rounded-[2rem] overflow-hidden border border-border/40 shadow-lg bg-secondary/10 relative group">
                        <img
                            src={`${API_URL}/${store.media.find(m => m.referenceType === 'store_banner')!.url}`}
                            alt="Store Hero Banner"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />
                    </div>
                )}

                {/* Dashboard Header Section */}
                <div className="bg-card border border-border/60 rounded-3xl p-8 shadow-sm relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl" />

                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
                        <div className="space-y-1.5">
                            <h4 className="text-xs font-bold text-primary uppercase tracking-[0.2em]">Operational Overview</h4>
                            <p className="text-sm text-muted-foreground max-w-md">Real-time health and performance metrics for this location.</p>
                        </div>
                        <div className="bg-secondary/20 p-4 rounded-2xl border border-border/40 backdrop-blur-sm">
                            <CommonSelect
                                value={store.status}
                                onValueChange={handleStatusUpdate}
                                options={
                                    store.status?.toLowerCase() === 'created'
                                        ? STORE_STATUS_OPTIONS
                                        : STORE_STATUS_OPTIONS.filter(opt => opt.value !== 'created')
                                }
                                disabled={statusLoading}
                                label="Availability Status"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-10 pt-10 border-t border-border/40 relative z-10">
                        <CommonCard label="Total Orders" value={store?.counts?.totalOrders?.toString() || "0"} icon={<ShoppingBag />} iconColor="#F43F5E" />
                        <CommonCard label="Completed Orders" value={store?.counts?.completedOrders?.toString() || "0"} icon={<CheckCircle2 />} iconColor="#10B981" />
                        <CommonCard label="Total Earnings" value={`${formatRand(store?.counts?.totalEarnings || 0)}`} icon={<Banknote />} iconColor="#3B82F6" />
                        <CommonCard label="Pending Payments" value={`${formatRand(store?.counts?.pendingPayments || 0)}`} icon={<Clock />} iconColor="#F59E0B" />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    {/* Main Info - Left 2 Columns */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Highlights Card */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <CommonCard
                                title="Opening Windows"
                                icon={<Clock />}
                                iconColor="hsl(var(--primary))"
                            >
                                <div className="space-y-4 pt-1">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-muted-foreground font-medium flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                            Opens
                                        </span>
                                        <span className="text-foreground font-extrabold tracking-tight">{formatTime12Hour(store.settings?.openingTime)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-muted-foreground font-medium flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                            Closes
                                        </span>
                                        <span className="text-foreground font-extrabold tracking-tight">{formatTime12Hour(store.settings?.closingTime)}</span>
                                    </div>
                                    <div className="pt-3 border-t border-border/40 flex items-center justify-center">
                                        {store.status === 'online' ? (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 text-[10px] text-green-700 font-bold uppercase tracking-wider">
                                                <CheckCircle2 className="w-3 h-3" /> Fully Operational
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-[10px] text-red-700 font-bold uppercase tracking-wider">
                                                <XCircle className="w-3 h-3" /> Operations Closed
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </CommonCard>

                            <CommonCard
                                title="Fulfillment Radius"
                                icon={<Target />}
                                iconColor="hsl(var(--primary))"
                            >
                                <div className="flex flex-col h-full justify-between pt-1">
                                    <div>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-black text-foreground tracking-tighter">
                                                {store.settings?.deliveryRadius || '0'}
                                            </span>
                                            <span className="text-lg font-bold text-muted-foreground/60">km</span>
                                        </div>
                                        <p className="text-[10px] text-muted-foreground mt-2 font-medium uppercase tracking-wide opacity-70">Maximum service area</p>
                                    </div>
                                    <div className="mt-4 h-1 w-full bg-secondary/30 rounded-full overflow-hidden">
                                        <div className="h-full bg-primary w-2/3 rounded-full" />
                                    </div>
                                </div>
                            </CommonCard>

                            <CommonCard
                                title="Extended Radius"
                                icon={<Target />}
                                iconColor="hsl(var(--primary))"
                            >
                                <div className="flex flex-col h-full justify-between pt-1">
                                    <div>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-black text-foreground tracking-tighter">
                                                {store.settings?.extendedDeliveryRadius || '0'}
                                            </span>
                                            <span className="text-lg font-bold text-muted-foreground/60">km</span>
                                        </div>
                                        <p className="text-[10px] text-muted-foreground mt-2 font-medium uppercase tracking-wide opacity-70">Extended service area</p>
                                    </div>
                                    <div className="mt-4 h-1 w-full bg-secondary/30 rounded-full overflow-hidden">
                                        <div className="h-full bg-primary/60 w-3/4 rounded-full" />
                                    </div>
                                </div>
                            </CommonCard>
                        </div>

                        {/* Description Section */}
                        <SectionCard title="Store Story" icon={<Info />}>
                            <div className="p-1 px-2">
                                <p className="text-muted-foreground leading-relaxed text-sm antialiased">
                                    {store.description || "No specific brand story has been shared for this location yet."}
                                </p>
                            </div>
                        </SectionCard>

                        {/* Manager Information */}
                        <SectionCard title="Manager Account" icon={<User />}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-1">
                                <DetailItem
                                    label="Account Holder"
                                    value={store.user?.fullName || "Awaiting Assignment"}
                                    icon={<User className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/5 transition-all"
                                />
                                <DetailItem
                                    label="Direct Email"
                                    value={store.user?.email || "N/A"}
                                    icon={<Mail className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/5 transition-all"
                                />
                            </div>
                        </SectionCard>

                        {/* Fulfillment & Logistics */}
                        <SectionCard title="Fulfillment & Logistics" icon={<Globe className="w-5 h-5" />}>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-1">
                                <DetailItem
                                    variant="horizontal"
                                    label="Take Away"
                                    value={
                                        <span className={`text-sm font-bold ${store.settings?.takeawayOrderStatus === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                                            {store.settings?.takeawayOrderStatus?.toUpperCase() || 'INACTIVE'}
                                        </span>
                                    }
                                />
                                <DetailItem
                                    variant="horizontal"
                                    label="Store Delivery"
                                    value={
                                        <div className="flex items-center gap-4">
                                            <span className={`text-sm font-bold ${store.settings?.deliveryOrderStatus === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                                                {store.settings?.deliveryOrderStatus?.toUpperCase() || 'INACTIVE'}
                                            </span>
                                            {store.settings?.deliveryOrderStatus === 'active' && (
                                                <span className="text-sm font-black text-primary">R {store.globalSetting?.fixDeliveryCharge || '0.00'}</span>
                                            )}
                                        </div>
                                    }
                                />
                                <DetailItem
                                    variant="horizontal"
                                    label="Courier Service"
                                    value={
                                        <div className="flex items-center gap-4">
                                            <span className={`text-sm font-bold ${store.settings?.courierOrderStatus === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                                                {store.settings?.courierOrderStatus?.toUpperCase() || 'INACTIVE'}
                                            </span>
                                            {store.settings?.courierOrderStatus === 'active' && (
                                                <span className="text-sm font-black text-primary">R {store.globalSetting?.fixCourierCharge || '0.00'}</span>
                                            )}
                                        </div>
                                    }
                                />
                                <DetailItem
                                    variant="horizontal"
                                    label="Commission"
                                    value={
                                        <span className="text-sm font-black text-primary">
                                            {store.settings?.commission ? `${store.settings?.commission}%` : '0%'}
                                        </span>
                                    }
                                />
                            </div>
                        </SectionCard>
                    </div>

                    {/* Sidebar Info - Right 1 Column */}
                    <div className="space-y-8 sticky top-6">
                        {/* Contact Details Card */}
                        <SectionCard title="Support Portal" icon={<Phone />}>
                            <div className="grid grid-cols-1 gap-y-6">
                                <DetailItem
                                    label="Primary Contact"
                                    value={`${store.countryCode || ''} ${store.contact}`}
                                    icon={<Phone />}
                                />
                                <div className="pt-2 border-t border-border/40">
                                    <DetailItem
                                        label="Record Last Sync"
                                        value={formatDateTime(store.updatedAt, 'default')}
                                        icon={<Calendar />}
                                    />
                                </div>
                            </div>
                        </SectionCard>

                        {/* Geolocation Section */}
                        <SectionCard title="Spatial Data" icon={<Globe />}>
                            <div className="space-y-4">
                                <div className="overflow-hidden border border-secondary/20 rounded-xl bg-secondary/5">
                                    {isLoaded ? (
                                        <GoogleMap
                                            mapContainerStyle={mapContainerStyle}
                                            center={{
                                                lat: parseFloat(store.latitude || '0'),
                                                lng: parseFloat(store.longitude || '0')
                                            }}
                                            zoom={15}
                                            options={{
                                                zoomControl: false,
                                                streetViewControl: false,
                                                mapTypeControl: false,
                                                fullscreenControl: false,
                                                draggable: false
                                            }}
                                        >
                                            <Marker
                                                position={{
                                                    lat: parseFloat(store.latitude || '0'),
                                                    lng: parseFloat(store.longitude || '0')
                                                }}
                                            />
                                        </GoogleMap>
                                    ) : (
                                        <div className="flex items-center justify-center w-full h-[200px] bg-secondary/10">
                                            <span className="text-[10px] text-muted-foreground animate-pulse uppercase font-bold tracking-widest">Map Loading...</span>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-secondary/5 rounded-xl p-3 border border-border/40">
                                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Latitude</p>
                                        <p className="text-sm font-black text-foreground">{store.latitude || "---"}</p>
                                    </div>
                                    <div className="bg-secondary/5 rounded-xl p-3 border border-border/40">
                                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Longitude</p>
                                        <p className="text-sm font-black text-foreground">{store.longitude || "---"}</p>
                                    </div>
                                </div>

                                <div className="bg-secondary/5 rounded-xl p-4 border border-border/40">
                                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3" /> Physical Address
                                    </p>
                                    <p className="text-sm text-foreground leading-relaxed font-medium">{store.address}</p>
                                </div>

                                <div className="pt-1 px-1 flex items-center gap-2 group cursor-help">
                                    <div className="w-2 h-2 rounded-full bg-primary/40 group-hover:bg-primary transition-colors" />
                                    <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-widest opacity-60 group-hover:opacity-100 transition-opacity">Geo-coordinates verified</span>
                                </div>
                            </div>
                        </SectionCard>

                        {/* Status Message */}
                        {/* <div className="animate-pulse duration-[3000ms]">
                            <InfoBanner status={store.status} />
                        </div> */}
                    </div>
                </div>
            </div>


        </Layout>
    );
};
